import { useCreateAdCampaign } from 'data/ad-campaign';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { CreateAdCampaignInput, EAdCreativeSource } from 'types/ad-campaign';

import {
  Step, StepContent, StepLabel, Stepper, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import Button from 'creative-components/CustomButtons/Button';

import { ADS_DASHBOARD_ROUTE } from 'components/AuthDataProvider/AuthDataProvider';
import {
  AD_CREATIVE_PROCESSING_LOADING_MODAL,
  useLoadingIndicatorContext,
} from 'components/LoadingIndicator/LoadingIndicatorProvider';
import LaunchStepIcon from 'components/SimpleLaunches/LaunchStepIcon';
import PublicLaunchIntro from 'components/SimpleLaunches/PublicLaunchIntro';
import SimpleCreateAdCampaignStep from 'components/SimpleLaunches/SimpleAdCampaign/SimpleCreateAdCampaignStep';
import SimpleCreateAccountStep from 'components/SimpleLaunches/SimpleCreateAccountStep';

import analyticsClient from 'utils/analytics';

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '0 auto',
    padding: '24px 30px 12px',
    position: 'relative',

    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },

    '@media (min-width: 600px)': {
      width: '580px', // 12 34px-width columns with 14px gutters
    },
    '@media (min-width: 900px)': {
      width: '840px', // 12 46px-width columns with 24px gutters
    },
    '@media (min-width: 1200px)': {
      width: '1104px', // 12 60px-width columns with 32px gutters
    },
    '@media (min-width: 1440px)': {
      width: '1344px', // 12 80px-width columns with 32px gutters (including on ends, so 12 * 80 + 12 * 32)
    },
  },
  stepLabel: {
    fontSize: '18px',
  },
  stepper: {
    '& .MuiStepConnector-vertical': {
      marginLeft: '17px',
    },
  },
  stepDescription: {
    marginBottom: '20px',
  },
  stepIconContainer: {
    paddingRight: '14px',
  },
  stepContent: {
    marginLeft: '17px',
    paddingLeft: '24px',
  },
  stepContainer: {

  },
  map: {
    minHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    margin: '0 0 20px',
  },
  goToDashboardButton: {
    marginTop: '20px',
    float: 'right',
  },
}));

const steps: { [key: string]: string } = {
  'Setup Your Digital Ad': 'Choose your audience, budget, and ad details so we can prepare the digital ad you want to launch.',
  'Create account': 'Review details required for your Harvist account',
  'Digital Ad Created': 'Your ad has been created! Pending ad network approval, your ad will start running soon.',
};

const SimpleCreateCampaignPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { showLoadingIndicatorModal } = useLoadingIndicatorContext() as any;
  const { createAdCampaign } = useCreateAdCampaign();

  const [activeStep, setActiveStep] = React.useState(0);
  const [createAdCampaignData, setCreateAdCampaignData] = React.useState<CreateAdCampaignInput | undefined>();
  const [paymentMethodId, setPaymentMethodId] = React.useState('');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  React.useEffect(() => {
    // Track GTM event
    analyticsClient.track('simple_digital_ad_creation_started');
  }, []);

  return (
    <div className={classes.root}>
      <PublicLaunchIntro
        label="How This Works"
        title="Launch Your Digital Ad"
        steps={[
          'Set your audience, budget, and ad details based on the area you want to reach.',
          'Create your account and add billing details.',
          'Submit the ad and start getting noticed by your audience!',
        ]}
        reassuranceText="Your digital ad will begin running and you can monitor performance and direct leads from your dashboard."
      />
      <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
        {Object.keys(steps).map((label) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={LaunchStepIcon}
              classes={{ label: classes.stepLabel, iconContainer: classes.stepIconContainer }}
            >
              {label}
            </StepLabel>
            <StepContent className={classes.stepContent}>
              <Typography className={classes.stepDescription}>{steps[label]}</Typography>
              <div className={classes.stepContainer}>
                {activeStep === 0 && (
                  <SimpleCreateAdCampaignStep
                    handleNext={handleNext}
                    setCreateAdCampaignData={setCreateAdCampaignData}
                  />
                )}

                {activeStep === 1 && (
                  <SimpleCreateAccountStep
                    submitButtonText="Start Ad"
                    paymentMethodId={paymentMethodId}
                    setPaymentMethodId={setPaymentMethodId}
                    handleNext={handleNext}
                    onLaunch={async () => {
                      if (createAdCampaignData!.creative.source === EAdCreativeSource.UserUpload) {
                        showLoadingIndicatorModal(AD_CREATIVE_PROCESSING_LOADING_MODAL);
                      }
                      await createAdCampaign({
                        ...createAdCampaignData!,
                        paymentMethodId, // Inject the payment method id here
                      });

                      // Track GTM event
                      await analyticsClient.track('simple_ad_campaign_creation_completed');
                    }}
                  />
                )}

                {activeStep === 2 && (
                  <Button
                    round
                    color="primary"
                    className={classes.goToDashboardButton}
                    onClick={() => history.push(ADS_DASHBOARD_ROUTE)}
                  >
                    Go to My Dashboard
                  </Button>
                )}
              </div>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </div>
  );
};

export default SimpleCreateCampaignPage;
